/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React from 'react'
import { type State } from '@hookstate/core'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Collapse,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterAutocompleteWorkSchedule from './FilterAutocompleteWorkSchedule'
import { useLang } from '~/hooks/useLang'

interface WorkScheduleLogFiltersProps {
  readonly hidden?: boolean
  readonly loadingButton: State<boolean>
  readonly labelButton?: State<string>
  readonly resetValues?: State<boolean>
  readonly userId: State<number | null>
  readonly workScheduleId: State<number | null>
  readonly action: State<string | null>
  readonly initialPeriod: State<string>
  readonly onPeriodChange: (value: string) => void
  readonly onStartDateChange: (value: string | null) => void
  readonly onFinishDateChange: (value: string | null) => void
  readonly clearFilters: () => void
  readonly searchButton?: () => void
}

const ACTION_OPTIONS = [
  { value: 'create', labelKey: 'Create' },
  { value: 'update', labelKey: 'Update' },
  { value: 'delete', labelKey: 'Delete' },
  { value: 'print_device_schedule', labelKey: 'Print Device Schedule' },
  { value: 'print_user_schedule', labelKey: 'Print User Schedule' },
]

export default function WorkScheduleLogFilters({
  hidden,
  loadingButton,
  labelButton,
  resetValues,
  userId,
  workScheduleId,
  action,
  initialPeriod,
  onStartDateChange,
  onFinishDateChange,
  onPeriodChange,
  clearFilters,
  searchButton,
}: WorkScheduleLogFiltersProps) {
  const { translate } = useLang()

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
      >
        <Grid container direction={'column'} display={'flex'} gap={1}>
          <Grid container direction={'row'} flexWrap={'wrap'}>
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="wsl-action-label">
                  {translate('action')}
                </InputLabel>
                <Select
                  labelId="wsl-action-label"
                  value={action.get() ?? ''}
                  label={translate('action')}
                  onChange={(item) =>
                    action.set(item.target.value || null)
                  }
                  endAdornment={
                    action.get() ? (
                      <IconButton
                        size="small"
                        onClick={() => action.set(null)}
                        sx={{ marginRight: 2 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  {ACTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {translate(option.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>



            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FilterAutocompleteFetch
                label={translate('User')}
                urlFetch={'/user/get'}
                dataRequest={'user'}
                size={'small'}
                disabled={false}
                onFetchChange={(value: number | null) => userId.set(value)}
                resetValues={resetValues?.get()}
                valueId={userId.get() ?? undefined}
              />
            </Grid>

            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FilterAutocompleteWorkSchedule
                onWorkScheduleChange={(value) => workScheduleId.set(value)}
                workScheduleId={workScheduleId.get() ?? null}
                disabled={false}
                label={translate('Scale')}
              />
            </Grid>

            <FilterSelectPeriod
              onStartDateChange={(value) =>
                onStartDateChange(value?.toJSON() ?? null)
              }
              onFinishDateChange={(value) =>
                onFinishDateChange(value?.toJSON() ?? null)
              }
              onPeriodChange={onPeriodChange}
              period={initialPeriod.get()}
              xsDateBetween={2}
              xsPeriod={2}
              hidePeriod={false}
              marginDateBetween={0}
              disableFuture={false}
              required
              requiredStartDate
              requiredEndDate
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction={'row'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          pt: 4,
          gap: 2,
        }}
      >
        <Button variant="outlined" onClick={clearFilters}>
          {translate('Clear Filters')}
        </Button>
        <LoadingButton
          loading={loadingButton.get()}
          variant="contained"
          onClick={searchButton}
        >
          {labelButton?.get() ?? translate('Search')}
        </LoadingButton>
      </Grid>
    </Collapse>
  )
}
